import { sortBy, uniq } from "lodash";

import {
  Content,
  Principal,
  SearchableResourceFilter,
  UserType,
  WorkspaceAcl,
  WorkspaceShareDefinitionVersion,
  WorkspaceUserAcl,
  WorkspaceUserRole,
} from "@examspark/types";

import { parseAwsJson } from "./misc";

export const isUserAdministrator = (
  userOrAcl: WorkspaceUserAcl | WorkspaceAcl | undefined,
) => {
  if (userOrAcl == null) return false;

  const acl = getAcl(userOrAcl);

  return isUserRole(acl, WorkspaceUserRole.Administrator);
};

export const isUserType = (
  userOrAcl: WorkspaceUserAcl | WorkspaceAcl | undefined,
  type: UserType,
) => {
  if (userOrAcl == null) return false;
  return getAcl(userOrAcl)?.role === type;
};

export const getFirstRoleOf = (
  principal: Principal,
  ...roles: WorkspaceUserRole[]
): WorkspaceUserRole | undefined => {
  if (principal == null) return undefined;

  const userRoles: WorkspaceUserRole[] = [];

  if (typeof principal === "string") {
    userRoles.push(principal as WorkspaceUserRole);
  } else {
    const acl = getAcl(principal);

    if (acl?.roleIds != null) {
      userRoles.push(...acl.roleIds);
    }
  }

  for (const testedRole of roles) {
    if (userRoles.includes(testedRole)) return testedRole;
  }

  return undefined;
};

export function isUserRole(
  principal: Principal,
  ...roles: WorkspaceUserRole[]
): boolean {
  return isUserExplicitlyRole(
    principal,
    ...roles,
    WorkspaceUserRole.Administrator,
  );
}

export function isUserExplicitlyRole(
  principal: Principal,
  ...roles: WorkspaceUserRole[]
) {
  return !!getFirstRoleOf(principal, ...roles);
}

export const getAcl = (user: WorkspaceUserAcl | WorkspaceAcl | string) =>
  typeof user === "object" && "id" in user
    ? parseAwsJson<WorkspaceAcl>(user.acl)
    : parseAwsJson<WorkspaceAcl>(user);

export const parseWorkspaceShareSk = (sk: string | undefined) => {
  if (sk == null) return {};
  const parts = sk?.split("#");

  if (parts[0] !== "workspace-share") return {};

  let appId: string | undefined, workspaceId: string | undefined;

  for (const part of parts) {
    const [id, sk] = part.split("/");
    switch (sk) {
      case "app":
        appId = id;
        break;
      case "workspace":
        workspaceId = id;
        break;
    }
  }

  return { appId, workspaceId };
};

export const createWorkspaceShareSk = ({
  workspaceId,
  appId,
}: {
  workspaceId?: string;
  appId?: string;
}) => {
  if (workspaceId == null && appId == null)
    throw new Error("Invalid workspace share parameters");

  const parts: string[] = ["workspace-share"];
  if (appId != null) parts.push(`${appId}/app`);
  if (workspaceId != null) parts.push(`${workspaceId}/workspace`);

  return parts.join("#");
};

export const createShareFilterFromShareDefinitionV2 = (
  definition: WorkspaceShareDefinitionVersion<2>,
) => {
  if (!definition.bankIds?.length && !definition?.courseIds?.length) {
    // nothing is shared, create a filter that will never match
    return {
      bankId: { eq: "__EMPTY__" },
    };
  }

  const or: SearchableResourceFilter[] = [];

  or.push(
    ...(definition.courseIds?.map((id) => ({ referencesIds: { eq: id } })) ??
      []),
  );
  or.push(...(definition.bankIds?.map((id) => ({ bankId: { eq: id } })) ?? []));

  return {
    or,
    maturity: { eq: "OPERATIONAL" },
  };
};

export const createShareFilterFromShareDefinitionV1 = (
  definition: WorkspaceShareDefinitionVersion<1>,
  courses: Content[],
): SearchableResourceFilter => {
  const or: SearchableResourceFilter[] = [];

  const hasTRS = definition?.banks?.some((b) => b.bankId === "tx-tcmpc/trs");

  for (const bankDefinition of definition?.banks ?? []) {
    const filter: SearchableResourceFilter = {
      bankId: { eq: bankDefinition.bankId },
      or: [],
    };
    filter.or?.push(...getSubjectFilters(bankDefinition.subjects));

    if (bankDefinition.courseIds != null) {
      filter.or?.push(
        ...sortBy(bankDefinition.courseIds).flatMap((courseId) => [
          { id: { eq: courseId } },
          { courseId: { eq: courseId } },
          { referencesIds: { eq: courseId } },
        ]),
        {
          and: [
            { sk: { ne: "course#v0" } },
            { sk: { ne: "unit#v0" } },
            {
              or: getCourseSubjectFilters(bankDefinition.courseIds, courses),
            },
          ],
        },
        {
          sk: { eq: "vocabulary" },
          or: sortBy(
            uniq(
              courses
                .filter((c) => bankDefinition.courseIds?.includes(c.id))
                .flatMap((c) => c.subject ?? []),
            ),
          ).map((subject) => ({
            subject: { eq: subject },
          })),
        },
      );
    }

    or.push(filter);

    if (bankDefinition.bankId === "tx-tcmpc/bluebonnet" && hasTRS) {
      or.push(crossBankCourseComponentsForBluebonnet(bankDefinition, courses));
    }
  }

  return {
    maturity: { eq: "OPERATIONAL" },
    or,
  };
};

const crossBankCourseComponentsForBluebonnet = (
  bankDefinition: NonNullable<
    WorkspaceShareDefinitionVersion<1>["banks"]
  >[number],
  courses: Content[],
) => {
  const filter: SearchableResourceFilter = {
    bankId: { eq: "tx-tcmpc/trs" },
    sk: { eq: "objectives-view#v0" },
    or: [],
  };

  filter.or?.push(...getSubjectFilters(bankDefinition.subjects));
  filter.or?.push(
    ...getCourseSubjectFilters(bankDefinition.courseIds, courses),
  );

  return filter;
};

const getSubjectFilters = (subjects: string[] | undefined) => {
  return (
    sortBy(subjects)?.map((subject) => ({
      subject: { eq: subject },
    })) ?? []
  );
};

const getCourseSubjectFilters = (
  courseIds: string[] | undefined,
  courses: Content[],
) => {
  if (courseIds == null) return [];
  return sortBy(
    courses
      .filter((c) => courseIds.includes(c.id))
      .map((course) => ({
        subject: { eq: course.subject?.[0] },
        grade: { eq: course.grade?.[0] },
      })),
    (f) => f.subject?.eq,
    (f) => f.grade?.eq,
  );
};
